import React from 'react';
import {
  TableListItem,
  Text,
  Image,
  IconButton,
  PopoverMenu,
  Box,
} from '@wix/design-system';

import { More } from '@wix/wix-ui-icons-common';
import { useBILogger } from '@wix/fe-essentials/react';
import {
  AUTOMATION_IDS,
  getAppContextMenuActionDataHook,
} from '../../../constants/automationIds';
import type { AppAction } from '../../../services/platform/platform.types';
import styles from './BusinessAppHeader.module.scss';
import { BI_EVENTS_IDS, BI_PARAMS } from '../../../constants/biEvents';
import { getBIReportData } from '../../../utils/biEvents';

export interface BusinessAppHeaderProps {
  appId: string;
  title: string;
  description: string;
  imageSrc: string;
  generalAppActions: AppAction[];
  showDivider: boolean;
  onContextMenuOpen: () => void;
  onContextMenuActionClick: (action: AppAction) => void;
  biSetupStatus: string;
}

export const BusinessAppHeader: React.FC<BusinessAppHeaderProps> = ({
  appId,
  title,
  description,
  imageSrc,
  generalAppActions,
  showDivider,
  onContextMenuOpen,
  onContextMenuActionClick,
  biSetupStatus,
}) => {
  const logger = useBILogger();

  return (
    <TableListItem
      dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_HEADER}
      verticalPadding="large"
      horizontalPadding="large"
      showDivider={showDivider}
      options={[
        {
          value: (
            <Box verticalAlign="middle" gap="SP2">
              <Box minWidth="36px">
                <Image
                  width="36px"
                  height="36px"
                  src={imageSrc}
                  alt={title}
                  transparent
                />
              </Box>

              <div className={styles.description}>
                <Text
                  dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_HEADER_TITLE}
                  size="medium"
                  weight="normal"
                >
                  {title}
                </Text>
                <Text
                  dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_HEADER_DESCRIPTION}
                  size="small"
                  weight="thin"
                  secondary
                  ellipsis
                  maxLines={2}
                >
                  {description}
                </Text>
              </div>
            </Box>
          ),
          width: 'auto',
          align: 'left',
        },
        {
          value: (
            <PopoverMenu
              dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_CONTEXT_MENU}
              showArrow
              placement="right"
              textSize="small"
              zIndex={1500}
              onShow={onContextMenuOpen}
              triggerElement={
                <IconButton priority="secondary" size="tiny">
                  <More />
                </IconButton>
              }
            >
              {generalAppActions.map((action, index) => {
                return (
                  <PopoverMenu.MenuItem
                    dataHook={getAppContextMenuActionDataHook(action.id)}
                    key={action.id}
                    text={action.title}
                    onClick={() => {
                      onContextMenuActionClick(action);
                      logger.log(
                        getBIReportData(BI_EVENTS_IDS.ACTION_CLICK, appId, {
                          section: BI_PARAMS.SECTIONS.THREE_DOT_MENU,
                          action_id: action.id,
                          target_cta: BI_PARAMS.TARGET_CTA.BUTTON_CLICKED,
                          action_rank_on_section: index + 1,
                          setup_status: biSetupStatus,
                        }),
                      );
                    }}
                  />
                );
              })}
            </PopoverMenu>
          ),
          align: 'right',
        },
      ]}
    />
  );
};
